import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { ServiceType } from "api/repositories/service/model/ServiceResponseModel";
import { DiscountManagementInjection } from "features/discountManagement/di/DiscountManagementInjection";
import { UserInDiscountEntity, DiscountResponseEntity } from "features/discountManagement/domain/entities/DiscountResponseEntity";
import { DiscountRequestEntity } from "features/discountManagement/domain/entities/DiscountRequestEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

class DiscountManagementInput implements Disposable {
  public fetchDiscounts = new BehaviorSubject<DiscountRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDiscountDetail = new BehaviorSubject<string>("");
  public createDiscount = new BehaviorSubject<any>(null);
  public updateDiscount = new BehaviorSubject<{
    values: any;
    currentUsers: UserInDiscountEntity[];
  }>(null);
  public deleteDiscount = new BehaviorSubject<string>("");
  public fetchServiceList = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchGroupList = new BehaviorSubject<BaseFilterListRequestModel & { service?: ServiceType }>(null);
  public fetchProductList = new BehaviorSubject<BaseFilterListRequestModel>(null);
  public fetchParentDiscounts = new BehaviorSubject<DiscountRequestEntity>({});

  [Symbol.dispose](): void {
    this.fetchDiscounts.complete();
    this.fetchDiscountDetail.complete();
    this.createDiscount.complete();
    this.updateDiscount.complete();
    this.deleteDiscount.complete();
    this.fetchServiceList.complete();
    this.fetchGroupList.complete();
    this.fetchProductList.complete();
    this.fetchParentDiscounts.complete();
  }
}

class DiscountManagementOutput implements Disposable {
  public savedDiscounts = new BehaviorSubject<Array<DiscountResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<DiscountResponseEntity | null>(null);
  public pagination = new BehaviorSubject<Paging>({ page: 0, limit: 10, total: 0 });
  public productPagination = new BehaviorSubject<Paging>({ page: 0, limit: 10, total: 0 });
  public serviceList = new BehaviorSubject<Array<BaseFilterListResponseModel>>([]);
  public groupList = new BehaviorSubject<Array<BaseFilterListResponseModel>>([]);
  public productList = new BehaviorSubject<Array<BaseFilterListResponseModel>>([]);
  public parentDiscounts = new BehaviorSubject<Array<DiscountResponseEntity>>([]);
  public parentDiscountsPagination = new BehaviorSubject<Paging>({ page: 0, limit: 10, total: 0 });

  public actionSuccess = new BehaviorSubject<'create' | 'update' | 'delete' | null>(null);
  public onError = new BehaviorSubject<string>("");

  public listLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public submitLoading = new BehaviorSubject<boolean>(false);
  public productLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedDiscounts.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.productPagination.complete();
    this.serviceList.complete();
    this.groupList.complete();
    this.productList.complete();
    this.parentDiscounts.complete();
    this.parentDiscountsPagination.complete();
    this.actionSuccess.complete();
    this.onError.complete();
    this.listLoading.complete();
    this.detailLoading.complete();
    this.submitLoading.complete();
    this.productLoading.complete();
  }
}

@injectable()
export class DiscountManagementViewModel extends BaseViewModel<
  DiscountManagementInput,
  DiscountManagementOutput
> {
  private useCase = DiscountManagementInjection.getDiscountManagementUseCase();

  constructor() {
    super(new DiscountManagementInput(), new DiscountManagementOutput());
  }

  protected override transform(): void {
    // Fetch discount list
    this.skipFirstInitial(this.input.fetchDiscounts, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getDiscounts(params);
        if (res?.data) {
          this.output.savedDiscounts.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error) {
        this.output.savedDiscounts.next([]);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Fetch parent discount list
    this.skipFirstInitial(this.input.fetchParentDiscounts, async (params) => {
      try {
        const res = await this.useCase.getDiscounts(params);
        if (res?.data) {
          this.output.parentDiscounts.next(res.data);
          this.output.parentDiscountsPagination.next(res.pagination);
        }
      } catch (error) {
        this.output.parentDiscounts.next([]);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });

    // Fetch discount detail
    this.skipFirstInitial(this.input.fetchDiscountDetail, async (code) => {
      if (!code) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getDiscountDetail(code);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    // Create discount
    this.skipFirstInitial(this.input.createDiscount, async (values) => {
      if (!values) return;
      try {
        this.output.submitLoading.next(true);
        const res = await this.useCase.createDiscount(values);
        if (res?.data) {
          this.output.actionSuccess.next('create');
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.submitLoading.next(false);
      }
    });

    // Update discount
    this.skipFirstInitial(this.input.updateDiscount, async (payload) => {
      if (!payload) return;
      const { values, currentUsers } = payload;
      try {
        this.output.submitLoading.next(true);
        const res = await this.useCase.updateDiscount(values, currentUsers);
        if (res?.data) {
          this.output.actionSuccess.next('update');
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.submitLoading.next(false);
      }
    });

    // Delete discount
    this.skipFirstInitial(this.input.deleteDiscount, async (code) => {
      if (!code) return;
      try {
        this.output.submitLoading.next(true);
        const res = await this.useCase.deleteDiscount(code);
        if (res?.data) {
          this.output.actionSuccess.next('delete');
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.submitLoading.next(false);
      }
    });

    // Fetch service list
    this.skipFirstInitial(this.input.fetchServiceList, async (params) => {
      try {
        const res = await this.useCase.fetchServiceList(params);
        if (res) {
          const list: Array<BaseFilterListResponseModel> = [
            { name: strings.GlobalLabels.All, code: strings.GlobalLabels.All },
            ...res,
          ];
          this.output.serviceList.next(list);
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });

    // Fetch group list
    this.skipFirstInitial(this.input.fetchGroupList, async (params) => {
      if (!params) return;
      try {
        const res = await this.useCase.fetchGroupList(params);
        if (res) {
          const list: Array<BaseFilterListResponseModel> = [
            { name: strings.GlobalLabels.All, code: strings.GlobalLabels.All },
            ...res,
          ];
          this.output.groupList.next(list);
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });

    // Fetch product list
    this.skipFirstInitial(this.input.fetchProductList, async (params) => {
      if (!params) return;
      try {
        this.output.productLoading.next(true);
        const res = await this.useCase.fetchProductList(params);
        if (res?.data) {
          this.output.productList.next(res.data);
          this.output.productPagination.next(res.pagination);
        }
      } catch (error) {
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.productLoading.next(false);
      }
    });
  }
}
